'use client';

import React, { useState, useEffect } from 'react';
import { FaCalculator, FaTag, FaMoneyBill, FaChartLine, FaUsers, FaPercentage, FaQuestionCircle } from 'react-icons/fa';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet';
import Chart from 'react-apexcharts';
import { useTheme } from 'next-themes';

const CyberMondayCalculator = () => {
  const [originalPrice, setOriginalPrice] = useState('');
  const [discountPercentage, setDiscountPercentage] = useState('');
  const [shippingCost, setShippingCost] = useState('');
  const [taxRate, setTaxRate] = useState('');
  const [quantity, setQuantity] = useState('');
  const [result, setResult] = useState(null);
  const [chartData, setChartData] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const { theme } = useTheme();

  const calculateSavings = () => {
    setError('');
    setLoading(true);

    const op = parseFloat(originalPrice) || 0;
    const dp = parseFloat(discountPercentage) || 0;
    const sc = parseFloat(shippingCost) || 0;
    const tr = parseFloat(taxRate) || 0;
    const qty = parseFloat(quantity) || 1;

    if (isNaN(op) || isNaN(dp) || isNaN(qty)) {
      setError('Please enter valid numbers');
      setLoading(false);
      return;
    }

    if (op <= 0 || dp < 0 || dp > 100 || qty <= 0) {
      setError('Please enter valid values. Price and quantity must be greater than 0, discount must be between 0-100%');
      setLoading(false);
      return;
    }

    const discountAmount = (op * dp) / 100;
    const discountedPrice = op - discountAmount;
    const totalDiscountedPrice = discountedPrice * qty;
    const taxAmount = (totalDiscountedPrice * tr) / 100;
    const totalCost = totalDiscountedPrice + sc + taxAmount;
    const totalSavings = ((op * qty) - totalCost);
    const averageSavingsPerItem = totalSavings / qty;

    if (totalSavings < 0) {
      const reasons = [];
      
      // Check if shipping cost is too high
      if (sc > (op * qty * 0.2)) { // If shipping is more than 20% of total
        reasons.push('Shipping cost is too high. Consider looking for free shipping options or checking if the shipping cost is reasonable for the item.');
      }

      // Check if tax rate is too high
      if (tr > 30) { // If tax rate is more than 30%
        reasons.push('Tax rate is unusually high. Check if the tax rate you entered is correct for your region.');
      }

      // Check if discount is too low
      if (dp < 10) { // If discount is less than 10%
        reasons.push('Discount percentage is too low. Look for better deals with higher discounts.');
      }

      // Check if quantity is too low
      if (qty === 1) {
        reasons.push('Consider buying in bulk to get better deals and potentially lower shipping costs.');
      }

      setError(`Your inputs show a negative savings. Here are some suggestions to improve your deal:
      ${reasons.join('\n\n')}`);
      setLoading(false);
      return;
    }

    setResult({
      discountAmount,
      discountedPrice,
      totalDiscountedPrice,
      taxAmount,
      totalCost,
      totalSavings,
      averageSavingsPerItem,
      shippingCost: sc,
      quantity: qty,
      originalPrice: op
    });

    // Update chart data
    const chartValues = {
      'Original Price': op * qty,
      'Discounted Price': totalDiscountedPrice,
      'Shipping': sc,
      'Tax': taxAmount
    };

    setChartData({
      options: {
        chart: {
          type: 'donut',
          animations: {
            enabled: true,
            easing: 'easeinout',
            speed: 800
          }
        },
        theme: {
          mode: theme === 'dark' ? 'dark' : 'light'
        },
        labels: Object.keys(chartValues),
        colors: ['#2196F3', '#4CAF50', '#FFC107', '#9C27B0'],
        legend: {
          position: 'bottom'
        },
        responsive: [{
          breakpoint: 480,
          options: {
            chart: {
              width: 300
            },
            legend: {
              position: 'bottom'
            }
          }
        }]
      },
      series: Object.values(chartValues)
    });

    setLoading(false);
  };

  const formatCurrency = (amount) => {
    return amount.toLocaleString('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-gray-900 dark:to-gray-800">
      <Helmet>
        <title>Cyber Monday Calculator - Calculate Your Cyber Monday Savings</title>
        <meta name="description" content="Calculate your potential savings during Cyber Monday with our advanced calculator. Includes discounts, shipping, tax, and bulk purchase calculations." />
        <meta property="og:title" content="Cyber Monday Calculator" />
        <meta property="og:description" content="Calculate your potential savings during Cyber Monday with our advanced calculator. Includes discounts, shipping, tax, and bulk purchase calculations." />
        <meta property="og:type" content="website" />
        <meta property="og:image" content="https://example.com/path-to-image.jpg" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Cyber Monday Calculator" />
        <meta name="twitter:description" content="Calculate your potential savings during Cyber Monday with our advanced calculator. Includes discounts, shipping, tax, and bulk purchase calculations." />
        <meta name="twitter:image" content="https://example.com/path-to-image.jpg" />
      </Helmet>

      <div className="max-w-7xl mx-auto px-4 py-12">
        <div className="calculator-header text-center mb-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl p-8 max-w-2xl mx-auto"
          >
            <div className="flex items-center justify-center mb-6">
              <FaCalculator className="text-4xl text-blue-500 dark:text-blue-400" />
              <h2 className="text-2xl font-bold ml-3">Cyber Monday Calculator</h2>
            </div>

            {error && (
              <div className="bg-red-50 dark:bg-red-900 p-4 rounded-lg mb-4">
                <p className="text-red-700 dark:text-red-400">{error}</p>
              </div>
            )}

            <form onSubmit={(e) => { e.preventDefault(); calculateSavings(); }} className="space-y-6">
              <div className="space-y-4">
                <div className="flex items-center border-2 border-gray-200 dark:border-gray-700 rounded-lg p-2">
                  <FaTag className="text-blue-500 dark:text-blue-400" />
                  <input
                    type="number"
                    placeholder="Original Price (₹)"
                    value={originalPrice}
                    onChange={(e) => {
                      const value = e.target.value.replace(/[^0-9.]/g, '');
                      setOriginalPrice(value);
                    }}
                    className="ml-3 w-full p-2 outline-none bg-gray-50 dark:bg-gray-900"
                  />
                </div>

                <div className="flex items-center border-2 border-gray-200 dark:border-gray-700 rounded-lg p-2">
                  <FaPercentage className="text-green-500 dark:text-green-400" />
                  <input
                    type="number"
                    placeholder="Discount Percentage (%)"
                    value={discountPercentage}
                    onChange={(e) => {
                      const value = e.target.value.replace(/[^0-9.]/g, '');
                      setDiscountPercentage(value);
                    }}
                    className="ml-3 w-full p-2 outline-none bg-gray-50 dark:bg-gray-900"
                  />
                </div>

                <div className="flex items-center border-2 border-gray-200 dark:border-gray-700 rounded-lg p-2">
                  <FaMoneyBill className="text-yellow-500 dark:text-yellow-400" />
                  <input
                    type="number"
                    placeholder="Shipping Cost (₹)"
                    value={shippingCost}
                    onChange={(e) => {
                      const value = e.target.value.replace(/[^0-9.]/g, '');
                      setShippingCost(value);
                    }}
                    className="ml-3 w-full p-2 outline-none bg-gray-50 dark:bg-gray-900"
                  />
                </div>

                <div className="flex items-center border-2 border-gray-200 dark:border-gray-700 rounded-lg p-2">
                  <FaChartLine className="text-purple-500 dark:text-purple-400" />
                  <input
                    type="number"
                    placeholder="Tax Rate (%)"
                    value={taxRate}
                    onChange={(e) => {
                      const value = e.target.value.replace(/[^0-9.]/g, '');
                      setTaxRate(value);
                    }}
                    className="ml-3 w-full p-2 outline-none bg-gray-50 dark:bg-gray-900"
                  />
                </div>

                <div className="flex items-center border-2 border-gray-200 dark:border-gray-700 rounded-lg p-2">
                  <FaUsers className="text-blue-500 dark:text-blue-400" />
                  <input
                    type="number"
                    placeholder="Quantity"
                    value={quantity}
                    onChange={(e) => {
                      const value = e.target.value.replace(/[^0-9]/g, '');
                      setQuantity(value);
                    }}
                    className="ml-3 w-full p-2 outline-none bg-gray-50 dark:bg-gray-900"
                  />
                </div>
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full bg-gradient-to-r from-blue-500 to-indigo-500 text-red-500 py-3 rounded-lg hover:from-blue-600 hover:to-indigo-600 transition duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? (
                  <div className="flex items-center justify-center">
                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-white mr-2"></div>
                    Calculating...
                  </div>
                ) : (
                  'Calculate Savings'
                )}
              </button>

              {result && (
                <div className="bg-blue-100 dark:bg-blue-900 p-4 rounded-lg mt-4">
                  <h3 className="font-semibold mb-2">Your Savings:</h3>
                  <div className="space-y-2">
                    <p className="text-blue-800 dark:text-blue-400 text-lg">Total Savings: {formatCurrency(result.totalSavings)}</p>
                    <p className="text-green-800 dark:text-green-400">Average Savings per Item: {formatCurrency(result.averageSavingsPerItem)}</p>
                    <p className="text-yellow-800 dark:text-yellow-400">Total Cost: {formatCurrency(result.totalCost)}</p>
                    <p className="text-purple-800 dark:text-purple-400">Original Total: {formatCurrency(result.originalPrice * result.quantity)}</p>
                  </div>
                </div>
              )}
            </form>
          </motion.div>
        </div>

        {/* Chart Section */}
        {Object.keys(chartData).length > 0 && (
          <div className="mt-12">
            <h3 className="text-2xl font-bold mb-6">Cost Breakdown</h3>
            <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg p-8">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                <div>
                  <Chart
                    options={chartData.options}
                    series={chartData.series}
                    type="donut"
                    width={600}
                  />
                </div>
                <div className="space-y-6">
                  <p className="text-gray-600 dark:text-gray-400">
                    <strong>Original Price:</strong> {formatCurrency(result.originalPrice * result.quantity)}
                  </p>
                  <p className="text-gray-600 dark:text-gray-400">
                    <strong>Discounted Price:</strong> {formatCurrency(result.totalDiscountedPrice)}
                  </p>
                  <p className="text-gray-600 dark:text-gray-400">
                    <strong>Shipping Cost:</strong> {formatCurrency(result.shippingCost)}
                  </p>
                  <p className="text-gray-600 dark:text-gray-400">
                    <strong>Tax Amount:</strong> {formatCurrency(result.taxAmount)}
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* How to Use Section */}
        <div className="mt-16">
          <h3 className="text-2xl font-bold mb-8">How to Use</h3>
          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg p-6">
            <ol className="space-y-6">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
                className="border-l-4 border-blue-500 pl-4"
              >
                <div className="flex items-center mb-2">
                  <span className="text-2xl font-bold text-blue-500 mr-3">1</span>
                  <h4 className="font-semibold">Enter Original Price</h4>
                </div>
                <p className="text-gray-600 dark:text-gray-400 mb-2">Input the original price of the item before any discounts.</p>
                <p className="text-green-500 dark:text-green-400 italic">Example: If the item costs ₹5,000, enter 5000</p>
              </motion.div>
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4 }}
                className="border-l-4 border-blue-500 pl-4"
              >
                <div className="flex items-center mb-2">
                  <span className="text-2xl font-bold text-blue-500 mr-3">2</span>
                  <h4 className="font-semibold">Set Discount Percentage</h4>
                </div>
                <p className="text-gray-600 dark:text-gray-400 mb-2">Enter the Cyber Monday discount percentage offered by the retailer.</p>
                <p className="text-green-500 dark:text-green-400 italic">Example: If the discount is 30%, enter 30</p>
              </motion.div>
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.6 }}
                className="border-l-4 border-blue-500 pl-4"
              >
                <div className="flex items-center mb-2">
                  <span className="text-2xl font-bold text-blue-500 mr-3">3</span>
                  <h4 className="font-semibold">Add Shipping Cost</h4>
                </div>
                <p className="text-gray-600 dark:text-gray-400 mb-2">Enter any shipping costs associated with your purchase.</p>
                <p className="text-green-500 dark:text-green-400 italic">Example: If shipping costs ₹500, enter 500</p>
              </motion.div>
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.8 }}
                className="border-l-4 border-blue-500 pl-4"
              >
                <div className="flex items-center mb-2">
                  <span className="text-2xl font-bold text-blue-500 mr-3">4</span>
                  <h4 className="font-semibold">Enter Tax Rate</h4>
                </div>
                <p className="text-gray-600 dark:text-gray-400 mb-2">Enter your local sales tax rate as a percentage.</p>
                <p className="text-green-500 dark:text-green-400 italic">Example: If tax rate is 18%, enter 18</p>
              </motion.div>
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 1.0 }}
                className="border-l-4 border-blue-500 pl-4"
              >
                <div className="flex items-center mb-2">
                  <span className="text-2xl font-bold text-blue-500 mr-3">5</span>
                  <h4 className="font-semibold">Set Quantity</h4>
                </div>
                <p className="text-gray-600 dark:text-gray-400 mb-2">Enter the number of items you want to purchase.</p>
                <p className="text-green-500 dark:text-green-400 italic">Example: If buying 2 items, enter 2</p>
              </motion.div>
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 1.2 }}
                className="border-l-4 border-blue-500 pl-4"
              >
                <div className="flex items-center mb-2">
                  <span className="text-2xl font-bold text-blue-500 mr-3">6</span>
                  <h4 className="font-semibold">Calculate Savings</h4>
                </div>
                <p className="text-gray-600 dark:text-gray-400 mb-2">Click the "Calculate Savings" button to see your potential savings.</p>
                <p className="text-green-500 dark:text-green-400 italic">Example: For OP = ₹5000, DP = 30%, SC = ₹500, TR = 18%, Q = 2, your total savings would be ₹3,080</p>
              </motion.div>
            </ol>
          </div>
        </div>

        {/* Features Section */}
        <div className="mt-16">
          <h3 className="text-2xl font-bold mb-8">Key Features</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg p-6 text-center"
            >
              <FaTag className="text-blue-500 dark:text-blue-400" />
              <h4 className="mt-3 font-semibold">Discount Analysis</h4>
              <p className="mt-2 text-gray-600 dark:text-gray-400">Calculate exact savings from Cyber Monday discounts</p>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
              className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg p-6 text-center"
            >
              <FaMoneyBill className="text-green-500 dark:text-green-400" />
              <h4 className="mt-3 font-semibold">Total Cost Breakdown</h4>
              <p className="mt-2 text-gray-600 dark:text-gray-400">Includes shipping, tax, and all other costs</p>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.6 }}
              className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg p-6 text-center"
            >
              <FaChartLine className="text-purple-500 dark:text-purple-400" />
              <h4 className="mt-3 font-semibold">Visual Comparison</h4>
              <p className="mt-2 text-gray-600 dark:text-gray-400">See how different components affect your total cost</p>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.8 }}
              className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg p-6 text-center"
            >
              <FaUsers className="text-blue-500 dark:text-blue-400" />
              <h4 className="mt-3 font-semibold">Bulk Purchase Calculator</h4>
              <p className="mt-2 text-gray-600 dark:text-gray-400">Calculate savings for multiple items</p>
            </motion.div>
          </div>
        </div>

        {/* Benefits Section */}
        <div className="mt-16">
          <h3 className="text-2xl font-bold mb-8">Benefits</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg p-6"
            >
              <h4 className="font-semibold mb-2">Smart Shopping</h4>
              <p className="text-gray-600 dark:text-gray-400">Make informed decisions about Cyber Monday purchases</p>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
              className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg p-6"
            >
              <h4 className="font-semibold mb-2">Cost Optimization</h4>
              <p className="text-gray-600 dark:text-gray-400">Identify the best deals and maximize savings</p>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.6 }}
              className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg p-6"
            >
              <h4 className="font-semibold mb-2">Budget Planning</h4>
              <p className="text-gray-600 dark:text-gray-400">Plan your Cyber Monday shopping budget effectively</p>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.8 }}
              className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg p-6"
            >
              <h4 className="font-semibold mb-2">Comparison Tool</h4>
              <p className="text-gray-600 dark:text-gray-400">Compare different purchase scenarios</p>
            </motion.div>
          </div>
        </div>

        {/* FAQ Section */}
        <div className="mt-16">
          <h3 className="text-2xl font-bold mb-8">Frequently Asked Questions</h3>
          <div className="space-y-4">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg p-6"
            >
              <div className="flex items-center">
                <FaQuestionCircle className="text-yellow-500 dark:text-yellow-400 mr-3" />
                <h4 className="font-semibold">What is Cyber Monday?</h4>
              </div>
              <p className="mt-2 text-gray-600 dark:text-gray-400">Cyber Monday is a major online shopping event that takes place on the Monday following Thanksgiving in the United States. It offers significant discounts on various products and services.</p>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg p-6"
            >
              <div className="flex items-center">
                <FaQuestionCircle className="text-yellow-500 dark:text-yellow-400 mr-3" />
                <h4 className="font-semibold">How does this calculator work?</h4>
              </div>
              <p className="mt-2 text-gray-600 dark:text-gray-400">The calculator helps you calculate your potential savings during Cyber Monday by considering the original price, discount percentage, shipping costs, tax, and quantity of items you want to purchase.</p>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5 }}
              className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg p-6"
            >
              <div className="flex items-center">
                <FaQuestionCircle className="text-yellow-500 dark:text-yellow-400 mr-3" />
                <h4 className="font-semibold">Can I use this for international purchases?</h4>
              </div>
              <p className="mt-2 text-gray-600 dark:text-gray-400">Yes, you can use this calculator for any purchase by adjusting the tax rate and shipping costs according to your country's requirements.</p>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.7 }}
              className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg p-6"
            >
              <div className="flex items-center">
                <FaQuestionCircle className="text-yellow-500 dark:text-yellow-400 mr-3" />
                <h4 className="font-semibold">How do I calculate my total savings?</h4>
              </div>
              <p className="mt-2 text-gray-600 dark:text-gray-400">The calculator automatically calculates your total savings by subtracting the discounted price (including shipping and tax) from the original price.</p>
            </motion.div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CyberMondayCalculator;
